/**
 * SPDX-FileCopyrightText: 2019 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import { getCapabilities } from '@nextcloud/capabilities';
export default class Config {
    _capabilities;
    constructor() {
        this._capabilities = getCapabilities();
    }
    /**
     * Get default share permissions, if any
     */
    get defaultPermissions() {
        return this._capabilities.files_sharing?.default_permissions;
    }
    /**
     * Is public upload allowed on link shares ?
     * This covers File request and Full upload/edit option.
     */
    get isPublicUploadEnabled() {
        return this._capabilities.files_sharing?.public?.upload === true;
    }
    /**
     * Get the federated sharing documentation link
     */
    get federatedShareDocLink() {
        return window.OC.appConfig.core.federatedCloudShareDoc;
    }
    /**
     * Get the default link share expiration date
     */
    get defaultExpirationDate() {
        if (this.isDefaultExpireDateEnabled && this.defaultExpireDate !== null) {
            return new Date(new Date().setDate(new Date().getDate() + this.defaultExpireDate));
        }
        return null;
    }
    /**
     * Get the default internal expiration date
     */
    get defaultInternalExpirationDate() {
        if (this.isDefaultInternalExpireDateEnabled && this.defaultInternalExpireDate !== null) {
            return new Date(new Date().setDate(new Date().getDate() + this.defaultInternalExpireDate));
        }
        return null;
    }
    /**
     * Get the default remote expiration date
     */
    get defaultRemoteExpirationDateString() {
        if (this.isDefaultRemoteExpireDateEnabled && this.defaultRemoteExpireDate !== null) {
            return new Date(new Date().setDate(new Date().getDate() + this.defaultRemoteExpireDate));
        }
        return null;
    }
    /**
     * Are link shares password-enforced ?
     */
    get enforcePasswordForPublicLink() {
        return window.OC.appConfig.core.enforcePasswordForPublicLink === true;
    }
    /**
     * Is password asked by default on link shares ?
     */
    get enableLinkPasswordByDefault() {
        return window.OC.appConfig.core.enableLinkPasswordByDefault === true;
    }
    /**
     * Is link shares expiration enforced ?
     */
    get isDefaultExpireDateEnforced() {
        return window.OC.appConfig.core.defaultExpireDateEnforced === true;
    }
    /**
     * Is there a default expiration date for new link shares ?
     */
    get isDefaultExpireDateEnabled() {
        return window.OC.appConfig.core.defaultExpireDateEnabled === true;
    }
    /**
     * Is internal shares expiration enforced ?
     */
    get isDefaultInternalExpireDateEnforced() {
        return window.OC.appConfig.core.defaultInternalExpireDateEnforced === true;
    }
    /**
     * Is there a default expiration date for new internal shares ?
     */
    get isDefaultInternalExpireDateEnabled() {
        return window.OC.appConfig.core.defaultInternalExpireDateEnabled === true;
    }
    /**
     * Is remote shares expiration enforced ?
     */
    get isDefaultRemoteExpireDateEnforced() {
        return window.OC.appConfig.core.defaultRemoteExpireDateEnforced === true;
    }
    /**
     * Is there a default expiration date for new remote shares ?
     */
    get isDefaultRemoteExpireDateEnabled() {
        return window.OC.appConfig.core.defaultRemoteExpireDateEnabled === true;
    }
    /**
     * Are users on this server allowed to send shares to other servers ?
     */
    get isRemoteShareAllowed() {
        return window.OC.appConfig.core.remoteShareAllowed === true;
    }
    /**
     * Is public sharing enabled ?
     */
    get isPublicShareAllowed() {
        return this._capabilities?.files_sharing?.public?.enabled === true;
    }
    /**
     * Is sharing my mail (link share) enabled ?
     */
    get isMailShareAllowed() {
        // eslint-disable-next-line camelcase
        return this._capabilities?.files_sharing?.sharebymail?.enabled === true
            // eslint-disable-next-line camelcase
            && this.isPublicShareAllowed === true;
    }
    /**
     * Get the default days to link shares expiration
     */
    get defaultExpireDate() {
        return window.OC.appConfig.core.defaultExpireDate;
    }
    /**
     * Get the default days to internal shares expiration
     */
    get defaultInternalExpireDate() {
        return window.OC.appConfig.core.defaultInternalExpireDate;
    }
    /**
     * Get the default days to remote shares expiration
     */
    get defaultRemoteExpireDate() {
        return window.OC.appConfig.core.defaultRemoteExpireDate;
    }
    /**
     * Is resharing allowed ?
     */
    get isResharingAllowed() {
        return window.OC.appConfig.core.resharingAllowed === true;
    }
    /**
     * Is password enforced for mail shares ?
     */
    get isPasswordForMailSharesRequired() {
        return this._capabilities.files_sharing?.sharebymail?.password?.enforced === true;
    }
    /**
     * Always show the email or userid unique sharee label if enabled by the admin
     */
    get shouldAlwaysShowUnique() {
        return this._capabilities.files_sharing?.sharee?.always_show_unique === true;
    }
    /**
     * Is sharing with groups allowed ?
     */
    get allowGroupSharing() {
        return window.OC.appConfig.core.allowGroupSharing === true;
    }
    /**
     * Get the maximum results of a share search
     */
    get maxAutocompleteResults() {
        return parseInt(window.OC.config['sharing.maxAutocompleteResults'], 10) || 25;
    }
    /**
     * Get the minimal string length
     * to initiate a share search
     */
    get minSearchStringLength() {
        return parseInt(window.OC.config['sharing.minSearchStringLength'], 10) || 0;
    }
    /**
     * Get the password policy configuration
     */
    get passwordPolicy() {
        return this._capabilities?.password_policy || {};
    }
    /**
     * Returns true if custom tokens are allowed
     */
    get allowCustomTokens() {
        return this._capabilities?.files_sharing?.public?.custom_tokens;
    }
}
